import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Dropdown, Spin, notification } from 'antd';
import type { UploadFile } from 'antd/lib/upload/interface';
import Icon from '@ant-design/icons';
import { ADMIN_ACCESS_LEVELS, REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING, REQUEST_TYPE_VISITOR, getVisitorRequestStatus } from 'core/utils/value';
import Status from 'components/Form/Status';
import { unwrapResult } from '@reduxjs/toolkit';
import { PushSuccess, ScheduleSuccess, TableFilters, VisitRequestResubmitActionPayload, VisitRequestReviewResponsePayload } from 'core/visitRequestReview/types';
import { RightBoxIcon, TriangleDownIcon, TriangleUpIcon } from 'components/Elements/CustomSVGIcon';
import FormDetails from 'components/Form/FormDetails';
import { formatSubmittedDateTime } from 'components/RequestTable/CellRenderers';
import { getVisitRequestReview, resubmitActionRequest, updateVisitRequestReview, checkRequestSession, overruleSession, fetchApprovers, pushToApprover, scheduleRequest, releaseSession, approveNsipVisitor } from 'core/visitRequestReview/visitRequestReviewApi';
import { AppDispatch, CombinedStates } from 'core/types';
import ActionModal, { ActionModalHandlers } from 'components/Modals/ActionModal';
import ConfirmApprove from 'components/Modals/ConfirmApprove';
import { isEmpty } from 'underscore';
import { setLastRequestAmended } from 'core/visitRequestForm/visitRequestFormSlice';
import { cancelAmendmentRequest } from 'core/visitRequestForm/visitRequestFormApi';
import { groupShiftsByShift, summariseShiftCounts } from 'core/visitRequestReview/scheduleShifts';
import { requestFormRoute } from 'core/utils/requestFormRoute';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import Main from '../../components/layout/Main';
import './VisitorRequestView.scss';
import ActionDropdown from './components/ActionDropdown';
import ViewTable from './components/ViewTable';
import LockedRequestModal from './components/LockedRequestModal';
import { Actions } from './components/ActionDropdown/requestActions';
import PushActionModal, { PushActionModalHandlers } from './components/PushActionModal';
import ScheduleModal, { ScheduleCompletePayload } from './components/ScheduleModal';
import ReviewVisitorModal from '../../components/Modals/ReviewVisitorModal';

interface ActionProps {
    statusId: number;
    title: string;
    subTitle?: string;
    description?: string;
    confirmButton: string;
    cancelButton: string;
    action: string;
    placeholder?: string;
}

const VisitorRequestView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigation = useNavigate();
    const location = useLocation();
    const { filters, fromForm, returnTo } = location.state as { filters: TableFilters, fromForm: boolean, returnTo?: string } || {};

    const { isFetching, visitRequestReview, success, isUpdating, statusData, lockedStatus, isPushing, isScheduling, isApprovingNsip, metadata, canAmend } = useSelector((state: CombinedStates) => state.visitRequestReview);
    const { lastRequestAmended } = useSelector((state: CombinedStates) => state.visitRequestForm);
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const toolbar = buildMainToolbar(userData?.role?.accessLevel);
    const [status, setStatus] = useState<number>(1);
    const [actionStatus, setActionStatus] = useState<ActionProps | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [modalApprovedVisible, setModalApprovedVisible] = useState<boolean>(false);
    const [modalPushVisible, setModalPushVisible] = useState<boolean>(false);
    const [isOverruleModalOpen, setIsOverruleModalOpen] = useState<boolean>(false);
    const [isScheduledModalOpen, setIsScheduledModalOpen] = useState<boolean>(false);
    const [isReviewVisitorModalOpen, setIsReviewVisitorModalOpen] = useState<boolean>(false);
    const statusLabels = ['Pending Approval', 'Rejected or Queried', 'Approved', 'Completed'];
    const actionButtonIcon = isDropdownOpen ? TriangleUpIcon : TriangleDownIcon;
    const pushActionModalRef = useRef<PushActionModalHandlers>(null);
    const actionModalRef = useRef<ActionModalHandlers>(null);

    const refreshApprovers = () => {
        if (visitRequestReview.encryptedId !== null) {
            dispatch(fetchApprovers({ id: visitRequestReview.encryptedId }));
        } else {
            notification.error({
                message: 'Error',
                description: 'Could not fetch approvers.',
            });
        }
    };

    const handleConfirmRedirect = (nextRequest: boolean) => {
        if (nextRequest && metadata?.nextRequestId) {
            navigation(`/visitor-view-request/${metadata.nextRequestId}`, { state: { filters } });
        } else if (!nextRequest) {
            let query = '';

            if (filters) {
                const { siteText, searchTerms, statusText, startDate, endDate } = filters;
                const params = new URLSearchParams();

                if (siteText && !isEmpty(siteText)) {
                    params.append('siteText', siteText.toString());
                }
                if (searchTerms && !isEmpty(searchTerms)) {
                    params.append('searchTerms', searchTerms.toString());
                }
                if (statusText && !isEmpty(statusText)) {
                    params.append('statusText', statusText.toString());
                }
                if (startDate) {
                    params.append('startDate', startDate.toString());
                }
                if (endDate) {
                    params.append('endDate', endDate.toString());
                }

                query = params.toString();
            }

            if (query) {
                navigation(`/dashboard?${query}`);
            } else {
                navigation('/dashboard');
            }
        }

        setModalApprovedVisible(false);
    };

    const handlePushAction = (selectedIds: string[]) => {
        if (selectedIds.length === 0 || selectedIds !== null) {
            setModalPushVisible(false);
            dispatch(pushToApprover({ id: String(visitRequestReview.encryptedId), approverIds: selectedIds }))
                .then(unwrapResult)
                .then((payload: PushSuccess) => {
                    if (payload.success) {
                        pushActionModalRef.current?.clear();
                    }
                });
        } else {
            notification.warning({
                message: 'Warning',
                description: 'Please select a new approver.',
            });
        }
    };

    const handleScheduleAction = (payload: ScheduleCompletePayload) => {
        if (payload.shifts.length === 0) {
            notification.warning({
                message: 'Warning',
                description: 'Please select at least one shift.',
            });
            return;
        }

        // Reconcile the officers on each selected shift (creating/deleting rows).
        dispatch(scheduleRequest({
            id: String(visitRequestReview.encryptedId),
            data: { shifts: payload.shifts },
        })).then(unwrapResult).then((_payload: ScheduleSuccess) => {
            if (_payload.statusData.inActiveActions.schedule) {
                setIsScheduledModalOpen(false);
            }
        }).catch(reportUnexpectedRejection);
    };

    const handleInitialLoad = () => {
        if (id !== undefined) {
            dispatch(getVisitRequestReview({
                id,
                filters,
            }));
        }
    };

    const checkSession = () => {
        if (id !== undefined) {
            dispatch(checkRequestSession({
                id,
            }));
        }
    };

    const handleBackLinkPress = () => {
        dispatch(releaseSession({ id }));
        if (returnTo) {
            // Opened from a report list (Ad Hoc / DAL) — return to that exact
            // list, re-opening the modal on the date it was viewed.
            navigation(returnTo);
        } else if (fromForm) {
            navigation('/dashboard');
        } else {
            navigation(-1);
        }
    };

    useEffect(() => {
        if (lastRequestAmended !== null && lastRequestAmended !== undefined) {
            dispatch(cancelAmendmentRequest({ id: lastRequestAmended.id, type: lastRequestAmended.type }));
        }

        handleInitialLoad();
        const interval = setInterval(() => {
            checkSession();
        }, 20000);

        window.onpopstate = () => {
            if (!lockedStatus?.isLocked && (lockedStatus?.lockedBy !== '' || lockedStatus?.lockedBy !== null || lockedStatus?.lockedBy !== undefined)) {
                dispatch(releaseSession({ id }));
            }
        };

        window.scrollTo({ top: 0, behavior: 'auto' });

        return () => {
            clearInterval(interval);
            dispatch(releaseSession({ id }));
        };
    }, [id]);

    // Fetch available approvers once review data is loaded so the
    // "Review Visitor" button visibility can be determined for work-type approvers.
    useEffect(() => {
        if (visitRequestReview.encryptedId) {
            dispatch(fetchApprovers({ id: visitRequestReview.encryptedId }));
        }
    }, [dispatch, visitRequestReview.encryptedId]);

    useEffect(() => {
        if (!isFetching && !isUpdating) {
            setStatus(statusData.currentStatusStep);
        }
    }, [isUpdating, isFetching, statusData.currentStatusStep]);

    useEffect(() => {
        if (lockedStatus?.isLocked) {
            setIsOverruleModalOpen(true);
        }
        if (lockedStatus?.overriden) {
            navigation('/dashboard', { replace: true });
        }
    }, [lockedStatus]);

    const handleOverrule = () => {
        if (id !== undefined) {
            dispatch(overruleSession({ id }));
        }
        setIsOverruleModalOpen(false);
    };

    const handleResubmit = async () => {
        if (id !== undefined) {
            dispatch(resubmitActionRequest({
                id: String(id),
            })).then(unwrapResult).then((payload: VisitRequestResubmitActionPayload) => {
                const { visitRequestId, type, isRetail } = payload;

                if (visitRequestId) {
                    // A resubmit walks the chain from the start, same as an
                    // amendment — and retail reorders that chain.
                    const { formPath, step } = requestFormRoute(type, isRetail);
                    navigation(`/${formPath}/${visitRequestId}/step/${step}`);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const invalidNsipVisitors = visitRequestReview.visitors?.filter((v) => v.isNsip && !v.isValidNsip) || [];

    const requestTypeLabel = visitRequestReview.type === REQUEST_TYPE_ACCESS ? 'access' : 'visitor';
    const VISITOR_REQUEST_STATUS = getVisitorRequestStatus(requestTypeLabel);

    const handleReviewVisitor = () => {
        setIsReviewVisitorModalOpen(true);
    };

    const handleApproveNsipVisitor = () => {
        const visitorIds = invalidNsipVisitors.map((v) => v.visitorId).filter((vid): vid is number => vid != null);
        if (visitorIds.length > 0 && visitRequestReview.encryptedId) {
            dispatch(approveNsipVisitor({
                id: visitRequestReview.encryptedId,
                visitorIds,
            })).then(unwrapResult).then(() => {
                setIsReviewVisitorModalOpen(false);
                // After NSIP visitors are approved, proceed to normal approve flow.
                // Cost approval only applies to visitor requests; access and
                // guarding skip straight to the confirmation step.
                setModalVisible(true);
                const isVisitorRequest = visitRequestReview.type !== REQUEST_TYPE_ACCESS
                    && visitRequestReview.type !== REQUEST_TYPE_GUARDING;
                if (isVisitorRequest && !visitRequestReview.hasCostApprove) {
                    setActionStatus(VISITOR_REQUEST_STATUS.cost_approval);
                } else {
                    setActionStatus(VISITOR_REQUEST_STATUS.confirm_approval);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleSelectAction = (action: string) => {
        setIsDropdownOpen(false);

        if (action === Actions.APPROVE) {
            // If there are invalid NSIP visitors, show the review modal first
            if (invalidNsipVisitors.length > 0) {
                setIsReviewVisitorModalOpen(true);
                return;
            }
            setModalVisible(true);
            // Cost approval only applies to visitor requests; access and
            // guarding skip straight to the confirmation step.
            const isVisitorRequest = visitRequestReview.type !== REQUEST_TYPE_ACCESS
                && visitRequestReview.type !== REQUEST_TYPE_GUARDING;
            if (isVisitorRequest && !visitRequestReview.hasCostApprove) {
                setActionStatus(VISITOR_REQUEST_STATUS.cost_approval);
            } else {
                setActionStatus(VISITOR_REQUEST_STATUS.confirm_approval);
            }
        }

        if (action === Actions.REJECT) {
            actionModalRef.current?.clear();
            setModalVisible(true);
            setActionStatus(VISITOR_REQUEST_STATUS.rejected);
        }

        if (action === Actions.QUERY) {
            actionModalRef.current?.clear();
            setModalVisible(true);
            setActionStatus(VISITOR_REQUEST_STATUS.query);
        }

        if (action === Actions.CANCEL) {
            actionModalRef.current?.clear();
            setModalVisible(true);
            setActionStatus(VISITOR_REQUEST_STATUS.cancel);
        }

        if (action === Actions.AMEND) {
            if (visitRequestReview.dateSubmitted !== undefined && visitRequestReview.dateSubmitted !== null) {
                dispatch(setLastRequestAmended({ id, type: visitRequestReview.type ?? REQUEST_TYPE_VISITOR }));
            }
            const { type } = visitRequestReview;
            // Retail reorders the access/guarding chains, so the first step of an
            // amendment is not the same route step for every site.
            const { formPath, step } = requestFormRoute(type, !!visitRequestReview.isRetail);
            navigation(`/${formPath}/${id}/step/${step}`, { state: { amending: true } });
        }

        if (action === Actions.RESUBMIT) {
            handleResubmit();
        }

        if (action === Actions.PUSH) {
            setModalPushVisible(true);
        }

        if (action === Actions.SCHEDULE) {
            setIsScheduledModalOpen(true);
        }
    };

    const handleRejectFromReview = () => {
        setIsReviewVisitorModalOpen(false);
        handleSelectAction(Actions.REJECT);
    };

    const renderActionsDropdown = () => (
        <ActionDropdown
            onMouseLeave={() => setIsDropdownOpen(false)}
            user={userData}
            requester={visitRequestReview.requester}
            approver={visitRequestReview.approver}
            costApprover={visitRequestReview.costApprover}
            hasCostApprove={visitRequestReview.hasCostApprove}
            availableApprovers={visitRequestReview.availableApprovers}
            onSelectAction={(action) => handleSelectAction(action)}
            statusData={statusData}
            hasInvalidNsipVisitors={visitRequestReview.hasInvalidNsipVisitors}
            allNsipVisitorsApproved={visitRequestReview.allNsipVisitorsApproved}
            requestType={visitRequestReview.type ?? undefined}
            canAmend={canAmend} />
    );

    const handleConfirmAction = (action: string | undefined, message: string | undefined, files?: UploadFile[], notes?: string) => {
        if (id !== undefined && action !== null) {
            // Create FormData if there are files
            const formData = new FormData();
            formData.append('action', action || '');
            if (message) {
                formData.append('message', message);
            }
            if (notes) {
                formData.append('approvalNotes', notes);
            }
            if (files && files.length > 0) {
                files.forEach((file) => {
                    if (file.originFileObj) {
                        formData.append('documents[]', file.originFileObj);
                    }
                });
            }

            const hasFilesOrNotes = (files && files.length > 0) || notes;
            dispatch(updateVisitRequestReview({
                id,
                data: hasFilesOrNotes ? formData : { action, message },
            })).then(unwrapResult).then((payload: VisitRequestReviewResponsePayload) => {
                if (payload.visitRequest && payload.statusData) {
                    actionModalRef.current?.clear();
                }
            }).catch(reportUnexpectedRejection);
        }
        setModalVisible(false);
    };

    useEffect(() => {
        if (success) {
            if (actionStatus?.action === 'approve' && status !== 1) {
                setModalApprovedVisible(true);
            }
            setStatus(actionStatus?.statusId || 1);
        }
    }, [success]);

    // Collapse the officer copies into one entry per logical shift for both the
    // summary count and the schedule modal.
    const shiftGroups = groupShiftsByShift(visitRequestReview.dates);
    const siteName = visitRequestReview.site?.storeCode
        ? `${visitRequestReview.site.name} (${visitRequestReview.site.storeCode})`
        : visitRequestReview.site?.name;
    const isApprovedStep = statusData.currentStatusStep === 3 || statusData.currentStatusStep === 4;

    const infoCard = [
        {
            id: 1,
            content: (
                <>
                    {visitRequestReview.guestRequesterName || visitRequestReview.requester?.name}
                    {visitRequestReview.requesterCompany && ` (${visitRequestReview.requesterCompany})`}
                    {visitRequestReview.onBehalfEmail && (
                        <>
                            <br />
                            <span className="on-behalf-label">
                                on behalf of {visitRequestReview.onBehalfEmail}
                            </span>
                        </>
                    )}
                    {/* One-off booking links: who generated the link for this guest. */}
                    {visitRequestReview.generatedBy?.name && (
                        <>
                            <br />
                            <span className="on-behalf-label">
                                generated by {visitRequestReview.generatedBy.name}
                            </span>
                        </>
                    )}
                </>
            ),
            title: 'Requester',
        },
        {
            id: 2,
            content: (
                <>
                    {/* A removed site has no page to link to. */}
                    {visitRequestReview.site?.encryptedId && !visitRequestReview.site.isRemoved ? (
                        <button
                            type="button"
                            className="site-link"
                            onClick={() => navigation(`/site/${visitRequestReview.site?.encryptedId}`)}>
                            {siteName}
                        </button>
                    ) : siteName}
                    {visitRequestReview.site?.isRemoved && (
                        <span className="site-removed"> (removed)</span>
                    )}
                    {visitRequestReview.site?.staticSiteCode && (
                        <>
                            <br />
                            <span className="site-static-code">
                                Site Code: {visitRequestReview.site.staticSiteCode}
                            </span>
                        </>
                    )}
                </>
            ),
            title: 'Site',
        },
        {
            id: 3,
            content: formatSubmittedDateTime(visitRequestReview.dateSubmitted),
            title: 'Date Submitted',
        },
        ...(isApprovedStep && visitRequestReview.approvedBy ? [{
            id: 5,
            content: (
                <>
                    {visitRequestReview.approvedBy.name}
                    <br />
                    <span className="approved-at">
                        {visitRequestReview.approvedAt ? formatSubmittedDateTime(visitRequestReview.approvedAt) : 'N/A'}
                    </span>
                </>
            ),
            title: 'Request Approved',
        }] : []),
        {
            id: 4,
            content: visitRequestReview.visitors?.length,
            title: 'Number of Visitors',
        },
        {
            id: 6,
            content: summariseShiftCounts(shiftGroups),
            title: 'Dates Requested',
        },
    ];

    const REQUEST_TITLE_MAP: Record<string, string> = {
        [REQUEST_TYPE_VISITOR]: 'Visitor Request',
        [REQUEST_TYPE_ACCESS]: 'VSOC Access Request',
        guarding_request: 'Guarding Request',
    };

    const requestTitle = (visitRequestReview.type && REQUEST_TITLE_MAP[visitRequestReview.type]) || 'Request';

    return (
        <Main
            className="login"
            title={<div>{requestTitle}</div>}
            subHeaderTitle={`${requestTitle} #${visitRequestReview.id}`}
            subHeaderHasBackLink
            subHeaderBackLinkCallback={handleBackLinkPress}
            subHeaderRightAction={(
                <Button type="link" className="next-request-button" onClick={() => metadata?.nextRequestId && navigation(`/visitor-view-request/${metadata.nextRequestId}`, { state: { filters } })}>
                    <span style={{ textDecorationLine: 'none', color: !metadata?.nextRequestId ? '#C0D1D8' : '', cursor: !metadata?.nextRequestId ? 'not-allowed' : '' }}>
                        Next Request
                    </span>
                    <RightBoxIcon s={!metadata?.nextRequestId ? '#C0D1D8' : ''} />
                </Button>
            )}
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            floatingHeader={false}
            showFooter={false}
            footerContent={<div style={{ lineHeight: '30px', fontSize: '16px', float: 'right' }}>&copy; copyright 2020</div>}
            floatingFooter>
            <Spin spinning={isUpdating || isFetching}>
                <div className="main-visitor-container">
                    <div className="visitor-request-view">
                        <Status currentStatus={Number(status)} labels={statusLabels} />
                        <div style={{ marginTop: 50 }}>
                            <h1>Overview</h1>
                            <div className="overview-container">
                                <div className="overview-card-container">
                                    {infoCard.map(card => (
                                        <div key={card.id} className="overview-card">
                                            <div className="overview-content">
                                                <span className="overview-text">{card.title}</span>
                                                <span className="overview-content-text">{card.content || 'N/A'}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Antd dropdown - consistency */}
                                <Dropdown
                                    dropdownRender={renderActionsDropdown}
                                    trigger={['click']}
                                    align={{ offset: [-104, 10] }}
                                    open={isDropdownOpen}>
                                    <Button
                                        type="primary"
                                        style={{
                                            width: 204,
                                            marginTop: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            paddingLeft: 40,
                                            paddingRight: 22,
                                        }}
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                        Actions
                                        <span className="vertical-line" />
                                        <Icon
                                            component={actionButtonIcon as unknown as React.ForwardRefExoticComponent<unknown>} />
                                    </Button>
                                </Dropdown>
                            </div>
                            {statusData.currentStatusStep !== 2 && statusData.currentStatusStep !== 1 && (
                                <div>
                                    <ViewTable data={visitRequestReview} />
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ backgroundColor: '#EDF2F4', alignItems: 'center', display: 'grid', width: '100vw', marginTop: 50 }}>
                        <FormDetails
                            details={visitRequestReview}
                            id={id}
                            isCostApproverByPass={visitRequestReview.costApproverByPass}
                            hasCostApprove={visitRequestReview.hasCostApprove}
                            canRevealContact={ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel)}
                            onReviewVisitor={
                                ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel)
                                || visitRequestReview.approver?.some(({ id: appId }) => appId === userData?.id)
                                || visitRequestReview.availableApprovers?.some(({ id: appId }) => appId === userData?.id)
                                || userData?.email === visitRequestReview.costApprover?.email
                                    ? handleReviewVisitor
                                    : undefined
                            } />
                    </div>
                    <div>
                        <ActionModal
                            ref={actionModalRef}
                            visible={modalVisible}
                            onConfirm={(action, message, files, notes) => handleConfirmAction(action, message, files, notes)}
                            onCancel={() => setModalVisible(false)}
                            action={actionStatus}
                            hideAttachments={visitRequestReview.type === REQUEST_TYPE_GUARDING} />
                        <ConfirmApprove
                            visible={modalApprovedVisible}
                            onConfirm={() => handleConfirmRedirect(true)}
                            onCancel={() => handleConfirmRedirect(false)} />
                        <div />
                    </div>
                </div>
                <PushActionModal
                    ref={pushActionModalRef}
                    isModalOpen={modalPushVisible}
                    handleCancel={() => setModalPushVisible(false)}
                    parentWorkType={visitRequestReview.pushActionData?.parentWorkType}
                    childrenWorkTypes={visitRequestReview.pushActionData?.childrenWorkTypes}
                    defaultApprover={visitRequestReview.pushActionData?.defaultApprover}
                    approvers={visitRequestReview.availableApprovers}
                    fetchData={refreshApprovers}
                    loading={isPushing}
                    handlePushToApprover={handlePushAction} />
                <ScheduleModal
                    shiftData={shiftGroups}
                    isModalOpen={isScheduledModalOpen}
                    handleCancel={() => setIsScheduledModalOpen(false)}
                    handleMultiShiftComplete={(payload) => handleScheduleAction(payload)}
                    loading={isScheduling}
                    isRetail={!!visitRequestReview.isRetail}
                    securityTeam={visitRequestReview.securityOfficers} />
                <LockedRequestModal
                    isModalOpen={isOverruleModalOpen}
                    handleCancel={() => setIsOverruleModalOpen(false)}
                    handleOverrule={handleOverrule} />
            </Spin>
            <ReviewVisitorModal
                visible={isReviewVisitorModalOpen}
                visitors={invalidNsipVisitors}
                loading={isApprovingNsip}
                onApprove={handleApproveNsipVisitor}
                onReject={handleRejectFromReview}
                onCancel={() => setIsReviewVisitorModalOpen(false)} />
        </Main>
    );
};

export default VisitorRequestView;

VisitorRequestView.propTypes = {};
